import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { bugReportAPI } from '../lib/api';
import { Bug, Loader2, ArrowLeft, AlertTriangle, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

export default function AdminBugReportsPage() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    if (!authLoading && (!user || !isAdmin)) { navigate('/login'); return; }
    if (authLoading) return;
    loadData();
  }, [user, isAdmin, authLoading, navigate]);

  const loadData = async () => {
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterSeverity) params.severity = filterSeverity;
      const [rRes, sRes] = await Promise.all([
        bugReportAPI.adminList(params),
        bugReportAPI.adminStats()
      ]);
      setReports(rRes.data.reports || []);
      setStats(sRes.data);
    } catch {
      toast.error('Failed to load bug reports');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!authLoading && user && isAdmin) {
      setLoading(true);
      loadData();
    }
  }, [filterStatus, filterSeverity]);

  const handleUpdate = async (id) => {
    try {
      await bugReportAPI.adminUpdate(id, editData);
      toast.success('Bug report updated');
      setEditingId(null);
      setEditData({});
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const severityColors = {
    low: 'bg-blue-500/10 text-blue-400',
    medium: 'bg-amber-500/10 text-amber-400',
    high: 'bg-orange-500/10 text-orange-400',
    critical: 'bg-red-500/10 text-red-400',
  };

  const statusColors = {
    open: 'bg-red-500/10 text-red-400',
    in_review: 'bg-amber-500/10 text-amber-400',
    confirmed: 'bg-orange-500/10 text-orange-400',
    in_progress: 'bg-blue-500/10 text-blue-400',
    resolved: 'bg-green-500/10 text-green-400',
    closed: 'bg-zinc-800 text-zinc-400',
    wont_fix: 'bg-zinc-800 text-zinc-500',
  };

  if (loading || authLoading) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-[#00E5FF]" /></div>;

  return (
    <div className="px-6 md:px-12 max-w-7xl mx-auto py-12 md:py-20" data-testid="admin-bug-reports-page">
      <div className="flex items-center gap-3 mb-8">
        <Button variant="ghost" onClick={() => navigate('/admin')} className="text-zinc-400 hover:text-white" data-testid="back-to-admin">
          <ArrowLeft className="h-4 w-4 mr-1" /> Admin
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-8">
        <Bug className="h-6 w-6 text-red-400" />
        <h1 className="text-4xl font-bold text-white font-['Outfit'] tracking-tight">Bug Reports</h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total', value: stats.total || 0, icon: Bug, color: 'text-red-400' },
          { label: 'Open', value: stats.open || 0, icon: AlertTriangle, color: 'text-amber-400' },
          { label: 'In Progress', value: stats.in_progress || 0, icon: Clock, color: 'text-blue-400' },
          { label: 'Resolved', value: stats.resolved || 0, icon: CheckCircle2, color: 'text-green-400' },
        ].map((s, i) => (
          <div key={i} className="p-4 bg-[#0A0A0A] border border-white/5 rounded-sm">
            <s.icon className={`h-4 w-4 ${s.color} mb-2`} />
            <div className="text-xl font-bold text-white font-['Outfit']">{s.value}</div>
            <div className="text-[10px] text-zinc-600 uppercase tracking-wider mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Severity breakdown */}
      {stats.by_severity && Object.keys(stats.by_severity).length > 0 && (
        <div className="flex gap-3 mb-6">
          {Object.entries(stats.by_severity).map(([sev, count]) => (
            <div key={sev} className={`px-3 py-1.5 rounded-sm text-xs font-bold ${severityColors[sev] || 'bg-zinc-800 text-zinc-400'}`}>
              {sev}: {count}
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 mb-6">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-44 bg-[#0A0A0A] border-zinc-800 text-white rounded-sm" data-testid="filter-status"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent className="bg-[#0A0A0A] border-white/10">
            <SelectItem value="all" className="text-zinc-300">All Statuses</SelectItem>
            {['open', 'in_review', 'confirmed', 'in_progress', 'resolved', 'closed', 'wont_fix'].map(s => (
              <SelectItem key={s} value={s} className="text-zinc-300">{s.replace('_', ' ')}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterSeverity} onValueChange={setFilterSeverity}>
          <SelectTrigger className="w-44 bg-[#0A0A0A] border-zinc-800 text-white rounded-sm" data-testid="filter-severity"><SelectValue placeholder="All Severities" /></SelectTrigger>
          <SelectContent className="bg-[#0A0A0A] border-white/10">
            <SelectItem value="all" className="text-zinc-300">All Severities</SelectItem>
            {['critical', 'high', 'medium', 'low'].map(s => (
              <SelectItem key={s} value={s} className="text-zinc-300 capitalize">{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Reports Table */}
      <div className="bg-[#0A0A0A] border border-white/5 rounded-sm overflow-hidden">
        {reports.length === 0 ? (
          <div className="text-center py-16"><p className="text-zinc-500">No bug reports found</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="border-b border-white/5">
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Title</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Reporter</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Severity</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Date</th>
                <th className="text-right px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Actions</th>
              </tr></thead>
              <tbody>
                {reports.map(r => (
                  <React.Fragment key={r.id}>
                    <tr className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-3">
                        <div className="text-sm text-white font-medium">{r.title}</div>
                        <div className="text-xs text-zinc-600 mt-0.5 line-clamp-1">{r.description}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-white">{r.user_name}</div>
                        <div className="text-xs text-zinc-500">{r.user_email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-sm font-bold ${severityColors[r.severity] || ''}`}>{r.severity}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-sm ${statusColors[r.status] || ''}`}>{r.status?.replace('_', ' ')}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-zinc-500">{new Date(r.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => {
                          setEditingId(editingId === r.id ? null : r.id);
                          setEditData({ status: r.status, admin_notes: r.admin_notes || '', resolution: r.resolution || '' });
                        }} className="text-[#00E5FF] text-xs" data-testid={`manage-bug-${r.id}`}>
                          {editingId === r.id ? 'Close' : 'Manage'}
                        </Button>
                      </td>
                    </tr>
                    {editingId === r.id && (
                      <tr className="border-b border-white/5 bg-[#050505]">
                        <td colSpan={6} className="px-4 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                            {r.steps_to_reproduce && (
                              <div className="p-3 bg-[#0A0A0A] border border-white/5 rounded-sm">
                                <div className="text-[10px] text-zinc-600 uppercase tracking-wider mb-1">Steps to Reproduce</div>
                                <div className="text-xs text-zinc-400 whitespace-pre-wrap">{r.steps_to_reproduce}</div>
                              </div>
                            )}
                            {r.expected_behavior && (
                              <div className="p-3 bg-[#0A0A0A] border border-white/5 rounded-sm">
                                <div className="text-[10px] text-zinc-600 uppercase tracking-wider mb-1">Expected</div>
                                <div className="text-xs text-zinc-400">{r.expected_behavior}</div>
                              </div>
                            )}
                            {r.actual_behavior && (
                              <div className="p-3 bg-[#0A0A0A] border border-white/5 rounded-sm">
                                <div className="text-[10px] text-zinc-600 uppercase tracking-wider mb-1">Actual</div>
                                <div className="text-xs text-zinc-400">{r.actual_behavior}</div>
                              </div>
                            )}
                          </div>
                          {r.page_url && <p className="text-xs text-zinc-500 mb-3">URL: {r.page_url}</p>}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Status</Label>
                              <Select value={editData.status} onValueChange={(v) => setEditData(p => ({ ...p, status: v }))}>
                                <SelectTrigger className="mt-1 bg-[#0A0A0A] border-zinc-800 text-white rounded-sm"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-[#0A0A0A] border-white/10">
                                  {['open', 'in_review', 'confirmed', 'in_progress', 'resolved', 'closed', 'wont_fix'].map(s => (
                                    <SelectItem key={s} value={s} className="text-zinc-300">{s.replace('_', ' ')}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Admin Notes</Label>
                              <Textarea value={editData.admin_notes} onChange={(e) => setEditData(p => ({ ...p, admin_notes: e.target.value }))}
                                className="mt-1 bg-[#0A0A0A] border-zinc-800 text-white rounded-sm resize-none" rows={2} />
                            </div>
                            <div>
                              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Resolution</Label>
                              <Textarea value={editData.resolution} onChange={(e) => setEditData(p => ({ ...p, resolution: e.target.value }))}
                                className="mt-1 bg-[#0A0A0A] border-zinc-800 text-white rounded-sm resize-none" rows={2} />
                            </div>
                          </div>
                          <Button onClick={() => handleUpdate(r.id)} className="mt-3 bg-[#00E5FF] text-black font-bold rounded-sm text-xs" data-testid={`save-bug-${r.id}`}>
                            Save Changes
                          </Button>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
